import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Plus, Share2, Loader2 } from "lucide-react";

export default function Squads() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [squads, setSquads] = useState([]);
  const [active, setActive] = useState(null);
  const [products, setProducts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", event: "prom", color_theme: "" });

  const load = () => api.squads().then(({ data }) => setSquads(data));
  useEffect(() => { if (user) load(); api.products({}).then(({ data }) => setProducts(data)); }, [user]);

  if (!user) return <div className="h-[50vh] flex flex-col items-center justify-center gap-4"><p className="font-display text-3xl">Sign in to plan a squad</p><Link to="/auth" className="bg-ink text-ivory px-6 py-3 font-label text-[10px] rounded-sm">Sign In</Link></div>;

  const create = async () => {
    if (!form.name) { toast("Name your squad"); return; }
    setCreating(true);
    try { const { data } = await api.createSquad(form); setSquads((s) => [data, ...s]); setForm({ name: "", event: "prom", color_theme: "" }); toast.success("Squad created"); }
    catch (e) { toast.error(errMsg(e)); } finally { setCreating(false); }
  };

  const openSquad = async (id) => { const { data } = await api.squad(id); setActive(data); };
  const addLook = async (pid) => { await api.addBoard(active.id, { product_id: pid }); openSquad(active.id); toast.success("Added to board"); };

  const shareLinks = (squad) => {
    const url = `${window.location.origin}/squads`;
    const text = encodeURIComponent(`Join my prom squad "${squad.name}" on Heather's Boutique! ${url}`);
    return { wa: `https://wa.me/?text=${text}`, sms: `sms:?body=${text}` };
  };

  return (
    <div data-testid="squads-page" className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-12">
      <p className="font-label text-[10px] text-rosemetal mb-2">Coordinate together</p>
      <h1 className="font-display text-5xl sm:text-6xl mb-10">Squad Planning</h1>

      {!user.school_verified && <p className="font-body text-sm text-[hsl(var(--destructive))] mb-6">Verify your school email in your account to create squads.</p>}

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-4">
          <div className="border border-border rounded-sm p-6">
            <h3 className="font-display text-2xl mb-4">New Squad</h3>
            <input data-testid="squad-name" placeholder="Squad name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm mb-3 outline-none focus:border-rosemetal" />
            <input data-testid="squad-color" placeholder="Color theme (e.g. champagne & noir)" value={form.color_theme} onChange={(e) => setForm({ ...form, color_theme: e.target.value })} className="w-full border border-border rounded-sm px-3 py-2.5 text-sm mb-3 outline-none focus:border-rosemetal" />
            <button data-testid="create-squad-btn" onClick={create} disabled={creating || !user.school_verified} className="w-full bg-ink text-ivory py-3 font-label text-[10px] rounded-sm flex items-center justify-center gap-2 disabled:opacity-40">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create</button>
          </div>
          {squads.map((s) => (
            <button key={s.id} data-testid={`squad-${s.id}`} onClick={() => openSquad(s.id)} className={`w-full text-left border rounded-sm p-5 transition-colors ${active?.id === s.id ? "border-rosemetal bg-blush" : "border-border hover:border-ink"}`}>
              <h4 className="font-display text-2xl">{s.name}</h4>
              <p className="font-body text-xs text-muted-foreground mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> {s.members.length} · {s.color_theme || s.event}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {!active ? <div className="border border-dashed border-border rounded-sm h-full min-h-[300px] flex items-center justify-center"><p className="font-body text-muted-foreground">Select or create a squad to build your shared board.</p></div> : (
            <div data-testid="squad-board">
              <div className="flex items-center justify-between mb-6">
                <div><h2 className="font-display text-4xl">{active.name}</h2><p className="font-body text-xs text-muted-foreground mt-1">{active.color_theme} · {active.members.map((m) => m.name).join(", ")}</p></div>
                <div className="flex gap-2">
                  <a href={shareLinks(active).wa} target="_blank" rel="noreferrer" data-testid="share-whatsapp" className="border border-border rounded-sm p-2.5"><Share2 className="w-4 h-4" /></a>
                </div>
              </div>
              {active.board?.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mb-8">
                  {active.board.map((b) => b.product && (
                    <Link key={b.id} to={`/product/${b.product_id}`} className="relative aspect-[3/4] overflow-hidden bg-blush">
                      <img src={b.product.images[0]} alt="" className="w-full h-full object-cover object-top" />
                      <span className="absolute bottom-2 left-2 glass text-ink px-2 py-0.5 font-label text-[8px] rounded-sm">{b.by}</span>
                    </Link>
                  ))}
                </div>
              )}
              <h3 className="font-display text-2xl mb-4">Add a look to the board</h3>
              <div className="grid grid-cols-4 gap-3">
                {products.slice(0, 8).map((p) => (
                  <button key={p.id} onClick={() => addLook(p.id)} className="group relative aspect-[3/4] overflow-hidden bg-blush">
                    <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 flex items-center justify-center transition-colors"><Plus className="w-5 h-5 text-ivory opacity-0 group-hover:opacity-100" /></div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
